/**
 * Composition Store - localStorage persistence for Composite Smart Assets
 *
 * MVP implementation using localStorage. Future versions may use IndexedDB
 * or backend API for multi-device sync.
 *
 * @see https://gitlab.com/smart-assets.io/smart-asset-primitives/gitlab-profile/-/blob/master/docs/SmartAssetSpec/SA-SPEC-007-Composition.md
 */

import type { CompositeSmartAsset } from "../types/valueFlows";

const STORAGE_KEY = "satchel_composite_smart_assets";

/**
 * Save a new composite Smart Asset to localStorage
 */
export function saveComposite(composite: CompositeSmartAsset): void {
  const existing = getComposites();
  const updated = [...existing.filter((c) => c.id !== composite.id), composite];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
}

/**
 * Get all composite Smart Assets from localStorage
 */
export function getComposites(): CompositeSmartAsset[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed as CompositeSmartAsset[];
  } catch {
    return [];
  }
}

/**
 * Get a single composite by ID
 */
export function getCompositeById(id: string): CompositeSmartAsset | undefined {
  const composites = getComposites();
  return composites.find((c) => c.id === id);
}

/**
 * Delete a composite Smart Asset by ID
 */
export function deleteComposite(id: string): boolean {
  const existing = getComposites();
  const filtered = existing.filter((c) => c.id !== id);
  if (filtered.length === existing.length) {
    return false; // Nothing was deleted
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  return true;
}

/**
 * Update an existing composite Smart Asset
 */
export function updateComposite(
  id: string,
  updates: Partial<Omit<CompositeSmartAsset, "id" | "type">>,
): CompositeSmartAsset | null {
  const existing = getComposites();
  const index = existing.findIndex((c) => c.id === id);
  if (index === -1) return null;

  const updated: CompositeSmartAsset = {
    ...existing[index],
    ...updates,
  };
  existing[index] = updated;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
  return updated;
}

/**
 * Clear all composites (useful for testing/reset)
 */
export function clearComposites(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Check if a composite name already exists
 */
export function compositeNameExists(name: string, excludeId?: string): boolean {
  const composites = getComposites();
  return composites.some((c) => c.name.toLowerCase() === name.toLowerCase() && c.id !== excludeId);
}
